import ToastMessage from "../../../../common/ToastMessage";
import approvalRequestsService from "../../../../services/procurementService/approvalRequestsService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_APPROVALREQUESTS } from "../../../../utils/constants";
import { ApprovalRequestInterface } from "../../../../models/ProcurementModule/ApprovalRequest";
import { ApprovalStatus } from "../../../../enums/approvalStatus";

interface UpdateStatusContext {
  previousUpdateStatus: ApprovalRequestInterface[];
}

export interface UpdateStatusInput {
  id: string;
  status: ApprovalStatus;
  comment: string;
}

const useUpdateApprovalStatus = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalRequestInterface,
    Error,
    UpdateStatusInput,
    UpdateStatusContext
  >({
    mutationFn: async (approvalRequest: UpdateStatusInput) => {
      const { id, status, comment } = approvalRequest;
      return approvalRequestsService.UpdateStatus(id, status, comment);
    },

    onMutate: async (updatedApprovalRequest: UpdateStatusInput) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_APPROVALREQUESTS],
      });

      const previousApprovalRequests =
        queryClient.getQueryData<ApprovalRequestInterface[]>([
          CACHE_KEY_APPROVALREQUESTS,
        ]) || [];

      queryClient.setQueryData<ApprovalRequestInterface[]>(
        [CACHE_KEY_APPROVALREQUESTS],
        (approvalRequests = []) =>
          approvalRequests.filter(
            (approvalRequest) =>
              approvalRequest.id !== updatedApprovalRequest.id
          )
      );

      return { previousUpdateStatus: previousApprovalRequests };
    },

    onSuccess: (savedApprovalRequest) => {
      ToastMessage.Success();
      queryClient.setQueryData<ApprovalRequestInterface[]>(
        [CACHE_KEY_APPROVALREQUESTS],
        (approvalRequests = []) =>
          approvalRequests.filter(
            (approvalRequest) => approvalRequest.id !== savedApprovalRequest.id
          )
      );
    },

    onError: (error, updatedApprovalRequest, context) => {
      ToastMessage.Failed();
      if (!context) return;

      queryClient.setQueryData<ApprovalRequestInterface[]>(
        [CACHE_KEY_APPROVALREQUESTS],
        context.previousUpdateStatus
      );
    },
  });
};

export default useUpdateApprovalStatus;
